import { selectShops, updateShop } from "app/redux/shopsSlice";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { searchLocation as searchLocationFromApi } from "app/redux/mapboxSlice";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { getFormValues } from "app/utils/getFormValues";
import { toast } from "react-toastify";
import { selectAccount } from "app/redux/accountSlice";

interface LocationSuggestion {
  name: string;
  latitude: number;
  longitude: number;
}

interface LocationState {
  addressInputText: string;
  locationSuggestions: LocationSuggestion[];
  selectedLocation?: LocationSuggestion;
  phoneValue: string;
  isDirty: boolean;
  formValues: {
    name: string;
    email: string;
    phone: string;
    address: string;
    taxPercentage: string;
    timeZoneId: string;
    timeZoneName: string;
  };
  originalValues: {
    name: string;
    email: string;
    phone: string;
    address: string;
    taxPercentage: string;
    timeZoneId: string;
    timeZoneName: string;
  };
}

export const SHOP_TIMEZONES = [
  { id: "America/Los_Angeles", name: "Pacific Time (PST/PDT)" },
  { id: "America/Denver", name: "Mountain Time (MST/MDT)" },
  { id: "America/Chicago", name: "Central Time (CST/CDT)" },
  { id: "America/New_York", name: "Eastern Time (EST/EDT)" },
  { id: "America/Anchorage", name: "Alaska Time" },
  { id: "Pacific/Honolulu", name: "Hawaii Time" },
];

const joinAddress = (shop: any) =>
  [shop?.addressLine1, shop?.addressLine2].filter(Boolean).join(", ");

export const useLocations = () => {
  const dispatch = useDispatch();
  const { paywallPermissions } = useSelector(selectAccount);
  const existingShops = useSelector(selectShops);
  const [shops, setShops] = useState<any[]>([]);
  const [locationStates, setLocationStates] = useState<
    Record<number, LocationState>
  >({});
  const [isCreatingOrUpdatingNewShop, setIsCreatingOrUpdatingNewShop] =
    useState(false);
  const [openShops, setOpenShops] = useState<Record<number, boolean>>({});
  const [isNewLocationModalOpen, setIsNewLocationModalOpen] = useState(false);

  const searchLocations = async (shopId: number, search: string) => {
    if (!search) {
      setLocationStates((prev) => ({
        ...prev,
        [shopId]: {
          ...prev[shopId],
          locationSuggestions: [],
        },
      }));
      return;
    }
    try {
      const { data } = await dispatch(
        searchLocationFromApi({ query: search }) as any
      ).unwrap();
      setLocationStates((prev) => ({
        ...prev,
        [shopId]: {
          ...prev[shopId],
          locationSuggestions: data,
        },
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const debouncedSearchLocations = useCallback(
    debounce((shopId: number, search: string) => {
      searchLocations(shopId, search);
    }, 300),
    []
  );

  const handleInputChange = (shopId: number, field: string, value: string) => {
    setLocationStates((prev) => {
      const currentState = prev[shopId];
      if (!currentState) return prev;

      const updatedFormValues = {
        ...currentState.formValues,
        [field]: value,
      };

      const isDirty =
        updatedFormValues.name !== currentState.originalValues.name ||
        updatedFormValues.email !== currentState.originalValues.email ||
        updatedFormValues.phone !== currentState.originalValues.phone ||
        updatedFormValues.address !== currentState.originalValues.address ||
        updatedFormValues.taxPercentage !==
          currentState.originalValues.taxPercentage ||
        updatedFormValues.timeZoneId !== currentState.originalValues.timeZoneId;

      return {
        ...prev,
        [shopId]: {
          ...currentState,
          formValues: updatedFormValues,
          isDirty,
        },
      };
    });
  };

  const handleTimezoneChange = (shopId: number, timeZoneId: string) => {
    const option = SHOP_TIMEZONES.find((zone) => zone.id === timeZoneId);
    setLocationStates((prev) => {
      const currentState = prev[shopId];
      if (!currentState) return prev;

      const updatedFormValues = {
        ...currentState.formValues,
        timeZoneId,
        timeZoneName: option?.name || timeZoneId,
      };

      const isDirty =
        updatedFormValues.name !== currentState.originalValues.name ||
        updatedFormValues.email !== currentState.originalValues.email ||
        updatedFormValues.phone !== currentState.originalValues.phone ||
        updatedFormValues.address !== currentState.originalValues.address ||
        updatedFormValues.taxPercentage !==
          currentState.originalValues.taxPercentage ||
        updatedFormValues.timeZoneId !== currentState.originalValues.timeZoneId;

      return {
        ...prev,
        [shopId]: {
          ...currentState,
          formValues: updatedFormValues,
          isDirty,
        },
      };
    });
  };

  const onLocationInputChange = (shopId: number, search: string) => {
    setLocationStates((prev) => {
      const currentState = prev[shopId];
      if (!currentState) return prev;

      const updatedFormValues = {
        ...currentState.formValues,
        address: search,
      };

      const isDirty =
        updatedFormValues.name !== currentState.originalValues.name ||
        updatedFormValues.email !== currentState.originalValues.email ||
        updatedFormValues.phone !== currentState.originalValues.phone ||
        updatedFormValues.address !== currentState.originalValues.address ||
        updatedFormValues.taxPercentage !==
          currentState.originalValues.taxPercentage ||
        updatedFormValues.timeZoneId !== currentState.originalValues.timeZoneId;

      return {
        ...prev,
        [shopId]: {
          ...currentState,
          addressInputText: search,
          formValues: updatedFormValues,
          isDirty,
        },
      };
    });

    debouncedSearchLocations(shopId, search);
  };

  const onLocationSelect = (shopId: number, locationName: string) => {
    const selectedLocation = locationStates[shopId]?.locationSuggestions.find(
      (loc) => loc.name === locationName
    );
    if (selectedLocation) {
      setLocationStates((prev) => ({
        ...prev,
        [shopId]: {
          ...prev[shopId],
          selectedLocation,
          addressInputText: selectedLocation.name,
          formValues: { ...prev[shopId].formValues, address: selectedLocation.name },
          locationSuggestions: [],
        },
      }));
    }
  };

  const onShopArchiveToggle = async (shop: any, isArchived: boolean) => {
    const type = isArchived ? "unarchive" : "archive";
    const shouldToggleArchive = window.confirm(
      `Are you sure you want to ${type} the ${shop.name} location?`
    );
    if (!shouldToggleArchive) {
      return;
    }
    try {
      await dispatch(
        updateShop({
          id: shop.id,
          archivedAt: isArchived ? null : new Date().toISOString(),
        }) as any
      ).unwrap();

      setShops((prev) => prev.filter((prevShop) => prevShop.id !== shop.id));

      toast.success(`Location ${type}d successfully`);
    } catch (error) {
      console.log(error);
      toast.error(`Failed to ${type} location`);
    }
  };

  const setPhoneValue = (shopId: number, value: string) => {
    setLocationStates((prev) => {
      const currentState = prev[shopId];
      if (!currentState) return prev;

      const updatedFormValues = {
        ...currentState.formValues,
        phone: value,
      };

      const isDirty =
        updatedFormValues.name !== currentState.originalValues.name ||
        updatedFormValues.email !== currentState.originalValues.email ||
        updatedFormValues.phone !== currentState.originalValues.phone ||
        updatedFormValues.address !== currentState.originalValues.address ||
        updatedFormValues.taxPercentage !==
          currentState.originalValues.taxPercentage ||
        updatedFormValues.timeZoneId !== currentState.originalValues.timeZoneId;

      return {
        ...prev,
        [shopId]: {
          ...currentState,
          phoneValue: value,
          formValues: updatedFormValues,
          isDirty,
        },
      };
    });

    setShops((prev) =>
      prev.map((shop) =>
        shop.id === shopId ? { ...shop, phone: value } : shop
      )
    );
  };

  const onSubmit = async (shopId: number, event: React.FormEvent) => {
    event.preventDefault();
    const locationState = locationStates[shopId];

    const formValues = getFormValues(event.target as HTMLFormElement);
    const { email, name, taxPercentage, address } = formValues;
    const phone = locationState.phoneValue;
    const timeZoneId = locationState.formValues.timeZoneId;
    const timeZoneName = locationState.formValues.timeZoneName;

    if (!email || !phone || !name || !address || !taxPercentage) {
      toast.error("Please fill in all fields");
      return;
    }

    if (phone.length < 11) {
      toast.error("Phone number doesn't seem to be right");
      return;
    }

    // Validate taxPercentage
    const taxPercentageValue = parseFloat(taxPercentage);
    if (
      isNaN(taxPercentageValue) ||
      taxPercentageValue < 0 ||
      taxPercentageValue > 100
    ) {
      toast.error("Please enter a valid tax percentage between 0 and 100");
      return;
    }

    const addressLine1 = address.split(",")[0];
    const addressLine2 = address.split(",").slice(1).join(",").trim();

    // Only touch coordinates when the address changed, so editing other fields
    // doesn't reset them to 0.
    let coordinates = {};
    if (
      locationState.selectedLocation &&
      locationState.selectedLocation.name === address
    ) {
      coordinates = {
        latitude: locationState.selectedLocation.latitude,
        longitude: locationState.selectedLocation.longitude,
      };
    } else if (address !== locationState.originalValues.address) {
      coordinates = { latitude: 0, longitude: 0 };
    }

    let payload = {
      id: shopId,
      email,
      phone,
      name,
      address_line_1: addressLine1,
      address_line_2: addressLine2,
      ...coordinates,
      taxPercentage: taxPercentageValue,
      timeZoneId,
      timeZoneName,
    };

    try {
      setIsCreatingOrUpdatingNewShop(true);
      const updatedShop = await dispatch(updateShop(payload) as any).unwrap();

      // Update the location states with new values after successful save
      setLocationStates((prev) => {
        const address = joinAddress(updatedShop);

        const newValues = {
          name: updatedShop.name || "",
          email: updatedShop.email || "",
          phone: updatedShop.phone || "",
          address: address || "",
          taxPercentage: updatedShop.taxPercentage?.toString() || "0",
          timeZoneId:
            updatedShop.timezone?.timeZoneId ||
            locationState.formValues.timeZoneId,
          timeZoneName:
            updatedShop.timezone?.timeZoneName ||
            locationState.formValues.timeZoneName,
        };

        return {
          ...prev,
          [shopId]: {
            ...prev[shopId],
            isDirty: false,
            formValues: { ...newValues },
            originalValues: { ...newValues },
            addressInputText: address || "",
            phoneValue: updatedShop.phone || "",
          },
        };
      });

      toast.success(`Location: ${name} updated successfully`);
    } catch (error) {
      toast.error(`Failed to update location: ${name}`);
      console.log(error);
    } finally {
      setIsCreatingOrUpdatingNewShop(false);
    }
  };

  const validShops = shops.filter((shop) => shop && locationStates[shop.id]);

  const toggleShop = (shopId: number) => {
    setOpenShops((prev) => ({ ...prev, [shopId]: !prev[shopId] }));
  };

  useEffect(() => {
    if (existingShops.length === 1) {
      toggleShop(existingShops[0].id);
    }
  }, []);

  useEffect(() => {
    const initialLocationStates = existingShops.reduce((acc, shop) => {
      const address = joinAddress(shop);

      const timeZoneId = shop.timezone?.timeZoneId || "America/Chicago";
      const knownZone = SHOP_TIMEZONES.find((zone) => zone.id === timeZoneId);
      const initialValues = {
        name: shop.name || "",
        email: shop.email || "",
        phone: shop.phone || "",
        address: address || "",
        taxPercentage: shop.taxPercentage?.toString() || "0",
        timeZoneId,
        timeZoneName:
          shop.timezone?.timeZoneName ||
          knownZone?.name ||
          "Central Time (CST/CDT)",
      };

      acc[shop.id] = {
        addressInputText: address || "",
        locationSuggestions: [],
        phoneValue: shop.phone || "",
        isDirty: false,
        formValues: { ...initialValues },
        originalValues: initialValues,
      };
      return acc;
    }, {} as Record<number, LocationState>);

    setLocationStates(initialLocationStates);
    setShops(existingShops);
  }, [existingShops]);

  return {
    validShops,
    locationStates,
    onLocationInputChange,
    onLocationSelect,
    onSubmit,
    isCreatingOrUpdatingNewShop,
    openShops,
    toggleShop,
    onShopArchiveToggle,
    isNewLocationModalOpen,
    setIsNewLocationModalOpen,
    setPhoneValue,
    paywallPermissions,
    handleInputChange,
    handleTimezoneChange,
    setOpenShops,
  };
};
